import { useMemo } from "react"
import { z } from "zod"
import { useLocation } from "@tanstack/react-router"
import { useCollectionsLight, useCollectionGroupsLight } from "~/hooks/useCollections"
import { useVariants } from "~/hooks/useVariants"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { useSkuStore } from "~/providers/sku-store-provider"
import { useDateRangeStore } from "~/providers/dateRangeStoreProvider"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { toUTCDateString } from "~/utils/datetime"
import { toStringArray } from "~/utils/helpers"
import { resolveDisplayedVariantIds } from "~/utils/variantScope"
import { api } from "~/utils/api"
import type { PageContext } from "~/server/api/routers/haki/types"

export function usePageContext(): PageContext | undefined {
  const { pathname, search: locationSearch } = useLocation()
  const variants = useVariants()
  const { data: rawCollections } = useCollectionsLight()
  const { data: rawCollectionGroups } = useCollectionGroupsLight()
  const warehouses = usePrimaryWarehouses()
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const [monthlyView] = useSafeLocalStorage("monthly-view", true, z.boolean())
  const weeklyStartDate = useDateRangeStore((state) => state.weeklyStartDate)
  const weeklyEndDate = useDateRangeStore((state) => state.weeklyEndDate)
  const monthlyStartDate = useDateRangeStore((state) => state.monthlyStartDate)
  const monthlyEndDate = useDateRangeStore((state) => state.monthlyEndDate)
  const dateContext = useMemo(
    () => ({
      startDate: toUTCDateString(monthlyView ? monthlyStartDate : weeklyStartDate),
      endDate: toUTCDateString(monthlyView ? monthlyEndDate : weeklyEndDate),
      monthly: monthlyView,
    }),
    [monthlyView, weeklyStartDate, weeklyEndDate, monthlyStartDate, monthlyEndDate],
  )
  const selectedWarehouseName = useMemo(
    () => warehouses.find((w) => w.id === selectedWarehouseId)?.name,
    [warehouses, selectedWarehouseId],
  )

  const currentProductId = useMemo(() => {
    const match = pathname.match(/\/product\/([^/]+)/)
    return match ? match[1] : null
  }, [pathname])

  const studioReportId = useMemo(() => {
    const match = pathname.match(/\/insights\/studio\/(\d+)/)
    return match ? match[1] : null
  }, [pathname])
  const { data: studioReport } = api.haki.report.getReport.useQuery(
    { id: Number(studioReportId) },
    { enabled: !!studioReportId, staleTime: Infinity },
  )

  const typedSearch = locationSearch as Record<string, unknown>
  const isGroup = typedSearch.isGroup === true

  return useMemo((): PageContext | undefined => {
    const collectionMatch = pathname.match(/\/seasons-collections\/(\d+)/)
    const page = pathname.split("/").filter(Boolean)[0] ?? "home"
    const isInboxPinboard = pathname === "/inbox" || undefined

    if (currentProductId) {
      const productVariants = variants.filter((v) => v.productId === currentProductId)
      const product = productVariants[0]
      // Raw location search: a single ?variantIds=X parses to a string, not an array — normalize.
      const searchVariantIds = toStringArray(typedSearch.variantIds)?.filter((id) => id !== "All")
      // Same resolution the product layout uses, so the scope Haki measures is the scope on screen.
      const displayedVariantIds = resolveDisplayedVariantIds(
        productVariants.map((v) => v.variantId),
        searchVariantIds,
      )
      const singleVariantId = displayedVariantIds.length === 1 ? displayedVariantIds[0] : undefined
      const selectedVariant = singleVariantId
        ? variants.find((v) => v.variantId === singleVariantId)
        : undefined
      return {
        page: "product",
        productId: currentProductId,
        productName: product?.productName,
        variantId: singleVariantId,
        variantName: selectedVariant?.variantName ?? selectedVariant?.productName,
        variantIds: displayedVariantIds.length > 0 ? displayedVariantIds : undefined,
        warehouseId: selectedWarehouseId,
        warehouseName: selectedWarehouseName,
        channel: selectedChannel,
        ...dateContext,
      }
    }
    if (collectionMatch?.[1]) {
      const matchedId = collectionMatch[1]
      if (isGroup) {
        const group = rawCollectionGroups?.find((g) => g.id.toString() === matchedId)
        return {
          page: "collection-group",
          collectionGroupId: matchedId,
          collectionGroupName: group?.name,
          warehouseId: selectedWarehouseId,
          warehouseName: selectedWarehouseName,
          channel: selectedChannel,
        }
      }
      const collection = rawCollections?.find((c) => c.id.toString() === matchedId)
      return {
        page: "collection",
        collectionId: matchedId,
        collectionName: collection?.name,
        warehouseId: selectedWarehouseId,
        warehouseName: selectedWarehouseName,
        channel: selectedChannel,
      }
    }
    if (studioReportId) {
      return {
        page,
        reportId: studioReportId,
        reportName: studioReport?.name,
        warehouseId: selectedWarehouseId,
        warehouseName: selectedWarehouseName,
        channel: selectedChannel,
      }
    }
    if (selectedWarehouseId || selectedChannel) {
      return {
        page,
        warehouseId: selectedWarehouseId,
        warehouseName: selectedWarehouseName,
        channel: selectedChannel,
        isInboxPinboard,
      }
    }
    return { page, isInboxPinboard }
  }, [
    pathname,
    currentProductId,
    variants,
    selectedWarehouseId,
    selectedWarehouseName,
    selectedChannel,
    dateContext,
    rawCollections,
    rawCollectionGroups,
    isGroup,
    typedSearch,
    studioReportId,
    studioReport,
  ])
}
